import { useState, useEffect, useRef, useCallback } from 'react';
import { useInactivityTimer } from './useInactivityTimer';
import { isMobileDevice } from '@/utils/deviceDetection';
import { checkBiometricAvailability } from '@/api/webauthn';

// Configurable via Vite env vars, with sensible defaults:
//   VITE_MOBILE_IDLE_TIMEOUT_MINUTES               (default: 3 min)
//   VITE_DESKTOP_IDLE_TIMEOUT_MINUTES              (default: 30 min)
//   VITE_MOBILE_VISIBILITY_LOCK_THRESHOLD_MINUTES  (default: 1 min)
//   VITE_DESKTOP_VISIBILITY_LOCK_THRESHOLD_MINUTES (default: 5 min)
//   VITE_DESKTOP_SESSION_LOCK_ENABLED              (default: false — desktop lock is opt-in)
const MOBILE_IDLE_MS =
  (Number(import.meta.env.VITE_MOBILE_IDLE_TIMEOUT_MINUTES) || 3) * 60_000;
const DESKTOP_IDLE_MS =
  (Number(import.meta.env.VITE_DESKTOP_IDLE_TIMEOUT_MINUTES) || 30) * 60_000;
const MOBILE_VISIBILITY_THRESHOLD_MS =
  (Number(import.meta.env.VITE_MOBILE_VISIBILITY_LOCK_THRESHOLD_MINUTES) || 1) * 60_000;
const DESKTOP_VISIBILITY_THRESHOLD_MS =
  (Number(import.meta.env.VITE_DESKTOP_VISIBILITY_LOCK_THRESHOLD_MINUTES) || 5) * 60_000;
const DESKTOP_LOCK_ENABLED = import.meta.env.VITE_DESKTOP_SESSION_LOCK_ENABLED === 'true';

// sessionStorage key used to persist the locked state across page reloads.
// sessionStorage is cleared when the tab is closed, so it does not survive
// a fresh browser session — only a reload within the same tab.
const LOCK_STORAGE_KEY = 'bolt_session_locked';

export interface SessionLockState {
  isLocked: boolean;
  /** True immediately when the page goes to background (visibilityState=hidden).
   * Used to blur content in the OS app switcher before the lock timer fires. */
  isPrivacyBlurred: boolean;
  biometricAvailable: boolean | null;
  unlock: () => void;
}

/**
 * Orchestrates session locking for Category A security requirements.
 *
 * - Detects inactivity (3 min mobile / 30 min desktop by default)
 * - Detects page-background via `visibilitychange` (1 min mobile / 5 min desktop by default)
 * - Checks WebAuthn platform authenticator availability
 * - Always active on mobile when `enabled` is true
 * - On desktop, additionally requires VITE_DESKTOP_SESSION_LOCK_ENABLED=true
 */
export function useSessionLock(enabled: boolean): SessionLockState {
  const isMobile = isMobileDevice();
  // Mobile: always active when authenticated.
  // Desktop: opt-in via VITE_DESKTOP_SESSION_LOCK_ENABLED.
  const effectivelyEnabled = enabled && (isMobile || DESKTOP_LOCK_ENABLED);
  const visibilityThresholdMs = isMobile ? MOBILE_VISIBILITY_THRESHOLD_MS : DESKTOP_VISIBILITY_THRESHOLD_MS;
  const [isLocked, setIsLocked] = useState<boolean>(
    // Restore lock state after a page reload — prevents reload-bypass
    () => sessionStorage.getItem(LOCK_STORAGE_KEY) === '1'
  );
  const [isBackgrounded, setIsBackgrounded] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState<boolean | null>(null);
  const hiddenAtRef = useRef<number | null>(null);

  // Check WebAuthn platform authenticator on mount
  useEffect(() => {
    checkBiometricAvailability().then(setBiometricAvailable);
  }, []);

  const triggerLock = useCallback(() => {
    if (effectivelyEnabled) {
      sessionStorage.setItem(LOCK_STORAGE_KEY, '1');
      setIsLocked(true);
    }
  }, [effectivelyEnabled]);

  const unlock = useCallback(() => {
    sessionStorage.removeItem(LOCK_STORAGE_KEY);
    setIsLocked(false);
  }, []);

  // Inactivity timer — disabled while already locked or not authenticated
  const idleTimeoutMs = isMobile ? MOBILE_IDLE_MS : DESKTOP_IDLE_MS;
  useInactivityTimer(idleTimeoutMs, triggerLock, effectivelyEnabled && !isLocked);

  // Privacy cover — activates when the window loses focus (window blur).
  // Debounced by 300 ms so that cross-application drag & drop does not
  // immediately cover content: if the user drags a file from the OS file
  // picker over BOLT within that window, dragenter fires and cancels the
  // timer before the cover ever appears.
  useEffect(() => {
    if (!effectivelyEnabled) return;

    let blurTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelTimer = () => {
      if (blurTimer !== null) { clearTimeout(blurTimer); blurTimer = null; }
    };

    const onBlur = () => {
      blurTimer = setTimeout(() => setIsBackgrounded(true), 300);
    };

    const onFocus = () => {
      cancelTimer();
      setIsBackgrounded(false);
    };

    // Cancel pending cover when a drag enters the window — handles the case
    // where the user picks a file from the file explorer (blur fires) and
    // then drags it over BOLT (dragenter fires before the 300 ms elapses).
    // Also clears the cover if it already appeared before the drag arrived.
    const onDragEnter = () => {
      cancelTimer();
      setIsBackgrounded(false);
    };

    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('dragenter', onDragEnter);
    return () => {
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('dragenter', onDragEnter);
      cancelTimer();
    };
  }, [effectivelyEnabled]);

  // Page-visibility lock: trigger when the user returns after being away
  // for longer than the visibility threshold (critical on mobile — covers
  // app-switch, screen lock, tab backgrounding).
  useEffect(() => {
    if (!effectivelyEnabled) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        setIsBackgrounded(true);
      } else if (document.visibilityState === 'visible') {
        // Always show the cover immediately on return — this prevents a flash of
        // unprotected content and fixes the mobile case where iOS batches the
        // hidden+visible events together (net state would otherwise be false).
        setIsBackgrounded(true);

        if (hiddenAtRef.current !== null) {
          const awayMs = Date.now() - hiddenAtRef.current;
          hiddenAtRef.current = null;

          if (awayMs >= visibilityThresholdMs) {
            // Long trip → lock. isLocked will keep isPrivacyBlurred=true;
            // clear isBackgrounded so it's not double-counted.
            triggerLock();
            setIsBackgrounded(false);
          } else {
            // Short trip → clear the cover after one animation frame so the
            // user doesn't see a jarring flash when quickly returning.
            requestAnimationFrame(() => setIsBackgrounded(false));
          }
        } else {
          requestAnimationFrame(() => setIsBackgrounded(false));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [effectivelyEnabled, triggerLock, visibilityThresholdMs]);

  return { isLocked, isPrivacyBlurred: isLocked || isBackgrounded, biometricAvailable, unlock };
}
